import ServicePageTemplate from './ServicePageTemplate';

const webDevData = {
  metaTitle: 'Web Development Company Dubai | Custom Web Applications UAE',
  metaDescription: 'Ascali provides professional web development services in Dubai, building responsive websites, web applications, portals, and custom business solutions for companies across the UAE.',
  bannerGradient: 'from-blue-600 via-blue-700 to-cyan-600',
  category: 'Technology Services',
  bannerTitle: 'Build Powerful Web Applications That Drive Real Results',
  bannerSubtitle: 'Modern, responsive, and scalable web solutions engineered to elevate your digital presence and convert visitors into loyal customers.',
  icon: (
    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  ),
  stats: [
    { value: '200+', label: 'Projects Delivered' },
    { value: '99.9%', label: 'Uptime Guarantee' },
    { value: '2x', label: 'Faster Load Times' },
    { value: '98%', label: 'Client Satisfaction' }
  ],
  overview: {
    title: 'Full-Stack Web Development Excellence',
    description: 'Ascali builds modern, high-performance web applications that combine sleek design with robust engineering. From landing pages to complex enterprise platforms, we craft digital experiences that engage users and drive measurable business growth.',
    highlights: [
      'Mobile-first, responsive design across all screen sizes',
      'SEO-optimized architecture for maximum organic visibility',
      'Scalable backend systems built for peak performance',
      'Secure, GDPR-compliant data handling practices',
      'Continuous integration and automated deployment pipelines'
    ]
  },
  deliverables: [
    'UI/UX Design Prototypes',
    'Production-Ready Source Code',
    'Backend API & Database Setup',
    'SEO & Analytics Integration',
    'Security Audit Report',
    'Deployment & Hosting Setup',
    'Documentation & Training'
  ],
  benefits: [
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
      title: 'Lightning-Fast Performance',
      description: 'Optimized code, lazy loading, and CDN integration ensure sub-second load times that keep users engaged and reduce bounce rates.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
      title: 'Mobile-First Design',
      description: 'Every application we build is designed for mobile first, ensuring flawless experiences on smartphones, tablets, and desktops.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
      title: 'Enterprise-Grade Security',
      description: 'Built-in security best practices including SSL, input validation, OWASP compliance, and regular vulnerability assessments.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
      title: 'SEO-Ready Architecture',
      description: 'Semantic HTML, structured data, and Core Web Vitals optimization give your site the best chance of ranking on page one.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>,
      title: 'Scalable Architecture',
      description: 'Microservices and cloud-native designs allow your application to scale seamlessly from hundreds to millions of users.'
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>,
      title: 'Quality Assurance',
      description: 'Comprehensive automated and manual testing across all browsers, devices, and edge cases before every deployment.'
    }
  ],
  process: [
    { step: '01', title: 'Discovery & Planning', description: 'We analyze your business goals, target audience, and technical requirements to create a detailed project roadmap.' },
    { step: '02', title: 'UI/UX Design', description: 'Our designers craft wireframes and interactive prototypes aligned with your brand and user expectations.' },
    { step: '03', title: 'Development & Testing', description: 'Agile sprints with continuous integration, code reviews, and rigorous automated testing at every stage.' },
    { step: '04', title: 'Launch & Support', description: 'Seamless deployment with monitoring, performance tuning, and dedicated post-launch support.' }
  ],
  technologies: ['React', 'Next.js', 'Angular', 'Vue.js', 'Node.js', 'Python', 'ASP.NET', 'C#', 'TypeScript', 'PostgreSQL', 'MongoDB', 'Redis', 'GraphQL', 'REST APIs', 'Docker', 'AWS'],
  cta: {
    title: 'Ready to Build Your Next Web Application?',
    subtitle: 'Join 200+ businesses that trust Ascali to deliver web solutions that perform, scale, and convert. Let\'s bring your vision to life.'
  },
  faqs: [
    {
      question: 'How long does it take to build a web application?',
      answer: 'Project timelines vary based on complexity. A standard informational website typically takes 4–6 weeks, while a full-featured web application can take 3–6 months. We provide a detailed timeline after our initial discovery session so you have full clarity before we begin.'
    },
    {
      question: 'What technologies do you use for web development?',
      answer: 'We work with a modern technology stack including React, Next.js, Angular, and Vue.js for front-end development; Node.js, Python, and ASP.NET for back-end; and PostgreSQL, MongoDB, and Redis for databases. We choose the best technology based on your specific requirements.'
    },
    {
      question: 'Do you provide ongoing maintenance and support?',
      answer: 'Yes. We offer flexible maintenance packages including regular security updates, performance monitoring, feature enhancements, and 24/7 emergency support. Our team stays involved long after launch to ensure your application keeps running smoothly.'
    },
    {
      question: 'Can you redesign or modernize my existing website?',
      answer: 'Absolutely. We specialize in website redesigns and legacy system modernization. We can migrate your existing content and functionality to a modern tech stack while improving performance, design, and user experience — with minimal downtime.'
    },
    {
      question: 'How do you ensure my web application is secure?',
      answer: 'Security is built into every layer of our development process. We follow OWASP guidelines, implement SSL encryption, conduct regular security audits, use parameterized queries to prevent SQL injection, and apply proper authentication and authorization patterns throughout.'
    },
    {
      question: 'Will my website be SEO-friendly?',
      answer: 'Yes. We build with SEO best practices from day one — semantic HTML, fast load times, structured data markup, mobile responsiveness, and clean URLs. We also integrate Google Analytics and Search Console so you can track performance from launch.'
    }
  ]
};

const WebDevelopmentPage = () => <ServicePageTemplate service={webDevData} />;
export default WebDevelopmentPage;
